/**
 * Hybrid Consent Manager
 *
 * Intelligently routes users to the appropriate consent management platform:
 * - EEA/UK/Switzerland users: Google's TCF-certified CMP (for personalized ads compliance)
 * - All other regions: Custom cookie consent banner (better UX, no TCF requirement)
 *
 * This ensures GDPR compliance while maintaining a great user experience globally.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CookieConsentBanner from './CookieConsentBanner';
import GoogleCMP, { showGoogleConsentForm } from './GoogleCMP';
import { detectUserLocation, type GeoLocation } from '../../utils/geoDetection';
import { logger } from "../../utils/logger";
import { useOverlayAdCoordinator } from '../../ads/OverlayAdCoordinator';

interface HybridConsentManagerProps {
    onPreferencesChange?: (preferences: any) => void;
}

const CONSENT_CLAIM_ID = 'hybrid-consent';
const LOADING_CHIP_DELAY_MS = 300;
/** Soft hold for GDPR CMP paint before APT popups proceed. */
const GDPR_CONSENT_LANE_HOLD_MS = 8000;
/** Absolute max consent-lane hold so a stuck CMP cannot block APT popups forever. */
const CONSENT_LANE_MAX_HOLD_MS = 30_000;

const HybridConsentManager: React.FC<HybridConsentManagerProps> = ({ onPreferencesChange }) => {
    const [geoLocation, setGeoLocation] = useState<GeoLocation | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showLoadingChip, setShowLoadingChip] = useState(false);
    const { acquire, release } = useOverlayAdCoordinator();
    const gdprReleaseScheduledRef = useRef(false);
    const gdprCleanupRef = useRef<(() => void) | null>(null);

    const releaseConsentLane = useCallback(() => {
        release('consent', CONSENT_CLAIM_ID);
    }, [release]);

    useEffect(() => {
        acquire('consent', CONSENT_CLAIM_ID);
        const maxHold = window.setTimeout(() => {
            releaseConsentLane();
        }, CONSENT_LANE_MAX_HOLD_MS);
        return () => {
            window.clearTimeout(maxHold);
            gdprCleanupRef.current?.();
            gdprCleanupRef.current = null;
            releaseConsentLane();
        };
    }, [acquire, releaseConsentLane]);

    useEffect(() => {
        if (!isLoading) {
            setShowLoadingChip(false);
            return;
        }
        const t = window.setTimeout(() => setShowLoadingChip(true), LOADING_CHIP_DELAY_MS);
        return () => window.clearTimeout(t);
    }, [isLoading]);

    useEffect(() => {
        let mounted = true;
        const initializeConsent = async () => {
            try {
                setIsLoading(true);
                const location = await detectUserLocation();
                if (!mounted) {
                    return;
                }
                setGeoLocation(location);
                if (process.env.NODE_ENV === 'development') {
                    logger.debug('🌍 Hybrid Consent Manager initialized:', { "arg0": {
                            location: location.countryCode || 'Unknown',
                            isGDPRRegion: location.isGDPRRegion,
                            cmpType: location.isGDPRRegion ? 'Google TCF-certified CMP' : 'Custom CMP',
                            detectionMethod: location.detectionMethod
                        } });
                }
                setIsLoading(false);
                // Non-GDPR: cookie banner may still show, but release consent lane so APT popups can proceed.
                if (!location.isGDPRRegion) {
                    releaseConsentLane();
                }
            }
            catch (err) {
                logger.error('Failed to detect user location:', err, {});
                if (!mounted) {
                    return;
                }
                setGeoLocation({
                    countryCode: null,
                    isGDPRRegion: false,
                    isEEA: false,
                    isUK: false,
                    isSwitzerland: false,
                    detectionMethod: 'unknown'
                });
                setError('Failed to detect location. Using standard consent banner.');
                setIsLoading(false);
                releaseConsentLane();
            }
        };
        void initializeConsent();
        return () => {
            mounted = false;
        };
    }, [releaseConsentLane]);

    const handleGoogleCmpLoaded = useCallback(() => {
        if (gdprReleaseScheduledRef.current) {
            return;
        }
        gdprReleaseScheduledRef.current = true;
        if (process.env.NODE_ENV === 'development') {
            logger.debug('✅ Google CMP ready for GDPR region');
        }
        const onConsentUpdate = () => {
            releaseConsentLane();
            window.removeEventListener('consent_update', onConsentUpdate);
        };
        window.addEventListener('consent_update', onConsentUpdate);
        const holdTimer = window.setTimeout(() => {
            window.removeEventListener('consent_update', onConsentUpdate);
            releaseConsentLane();
        }, GDPR_CONSENT_LANE_HOLD_MS);
        gdprCleanupRef.current = () => {
            window.clearTimeout(holdTimer);
            window.removeEventListener('consent_update', onConsentUpdate);
        };
    }, [releaseConsentLane]);

    if (isLoading) {
        if (!showLoadingChip) {
            return null;
        }
        return (
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 9998,
                    bgcolor: 'background.paper',
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                    Initializing consent management...
                </Typography>
            </Box>
        );
    }

    if (error && process.env.NODE_ENV === 'development') {
        logger.error('Consent Manager Error:', error, {});
    }

    const shouldUseGoogleCMP = geoLocation?.isGDPRRegion ?? false;
    return (
        <>
            {shouldUseGoogleCMP ? (
                <GoogleCMP onConsentLoaded={handleGoogleCmpLoaded} />
            ) : (
                <CookieConsentBanner onPreferencesChange={onPreferencesChange} />
            )}
        </>
    );
};

export default HybridConsentManager;

export async function openConsentPreferences(): Promise<void> {
    const location = await detectUserLocation();
    if (location.isGDPRRegion) {
        showGoogleConsentForm();
    } else {
        const event = new Event('openCookieSettings');
        window.dispatchEvent(event);
    }
}

if (typeof window !== 'undefined') {
    (window as Window & { openConsentPreferences?: typeof openConsentPreferences }).openConsentPreferences =
        openConsentPreferences;
}
